/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { getSituacaoInscricaoByDescription, SituacaoInscricao } from './../../../enums/situacao-inscricao'
import { AfterViewInit, Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs'
import { filter } from 'rxjs/operators'
import { ListagensDiversasService } from '../../../services/listagens-diversas.service'
import { ActivatedRoute } from '@angular/router'
import { ListagemSituacao } from 'src/app/models/listagem-situacao'
import { DatePipe } from '@angular/common'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { ConcursoService } from 'src/app/services/concurso.service'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'
import { MaskUtils } from 'src/app/utils/mask.utils'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-situacao-inscricao',
  templateUrl: './situacao-inscricao.component.html',
  styleUrls: ['./situacao-inscricao.component.scss'],
})
export class SituacaoInscricaoComponent implements AfterViewInit, OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent

  totalElements: number = 0
  sortField = 'id'
  sortDirection = 'desc'

  colunasGrid: string[] = [
    'id',
    'nome',
    'cpf',
    'protocolo',
    'pago',
    'situacao',
    'motivo',
    'autor',
    'data',
    'concluida',
  ]
  dataSource: MatTableDataSource<ListagemSituacao> = new MatTableDataSource()

  idConcurso?: number
  concursoTitulo?: string
  filtro: string = ''

  datepipe: DatePipe = new DatePipe('pt-BR')

  optSituacao = SituacaoInscricao
  dialogRef: any
  identificacaoFC: any
  mascaraVazio: string = ''

  constructor(
    private listagensDiversasService: ListagensDiversasService,
    private route: ActivatedRoute,
    private unespCoreMessageService: UnespCoreMessageService,
    private readonly concursoService: ConcursoService,
    private unespCoreAuthService: UnespCoreAuthService
  ) {}

  ngOnInit(): void {
    this.paginator.pageSize = 10
    ;(this.route.parent?.params || this.route.params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.idConcurso = params['id']
      this.getData()
    })
  }

  ngAfterViewInit() {
    // server-side search
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe()

    // on sort change, reset to first page and reload
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    // on paginate events, load a new page
    this.paginator?.page.pipe(tap(() => this.getData()), takeUntilDestroyed(this.destroyRef)).subscribe()
  }
  salvar(row: ListagemSituacao) {
    if (row.situacao == 'NAO_CONFIRMADA') {
      this.unespCoreMessageService.showMessageWarning(`Informe a situação do candidato ${row.nome}`)
    } else if (row.situacao == 'INDEFERIDA' && (!row.motivo || row.motivo.trim() == '')) {
      this.unespCoreMessageService.showMessageError(`Informe o motivo para do Indeferimento de ${row.nome}`)
    } else {
      this.listagensDiversasService
        .salvarSituacaoInscricao(this.idConcurso, row.id, row.situacao, row.motivo ?? '')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          if (value) {
            row.autor = value.autor
            row.motivo = value.motivo
            row.data = value.data
            this.unespCoreMessageService.showMessageSuccess(`${row.nome}, situação registrada com sucesso`)
          } else {
            this.unespCoreMessageService.showMessageError(`Falha ao registrar, ${row.nome}`)
          }
        })
    }
  }

  private getData() {
    this.listagensDiversasService
      .getSituacaoInscricao(
        this.idConcurso!,
        this.searchInput?.value,
        this.filtro,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sortField,
        this.sortDirection
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let inscricoes = data['content'] as ListagemSituacao[]
        this.dataSource = new MatTableDataSource(
          inscricoes.map(value => {
            value.situacao = getSituacaoInscricaoByDescription(value.situacao)
            return value
          })
        )
        this.totalElements = data['totalElements']
      })
      this.concursoService.getTitulo(this.idConcurso).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
        this.concursoTitulo = resp.concursoTitulo
      })
  }

  exportar() {
    this.listagensDiversasService
      .getSituacaoInscricao(this.idConcurso!, this.searchInput?.value, this.filtro, 0, this.totalElements)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        let recursos: ListagemSituacao[] = data['content']
        let content = `"#","NOME","CPF","PAGO","SITUAÇÃO","MOTIVO","AUTOR","DATA"\r\n`
        recursos.forEach(row => {
          content += `"${row.id}","${MaskUtils.preferSocialName(row.nome, row.nomeSocial)}","${MaskUtils.maskCpf(row.cpf)}","${row.pago ? 'Sim' : 'Não'}","${
            row.situacao
          }","${row.motivo == null ? '' : row.motivo}","${row.autor == null ? '' : row.autor}","${
            row.data == null ? '' : this.datepipe.transform(row.data, 'dd/MM/YYYY HH:mm:ss')
          }"\r\n`
        })
        let binaryData = []
        binaryData.push(content)
        let downloadLink = document.createElement('a')
        downloadLink.setAttribute('download', `listagem/situacao-${this.idConcurso}.csv`)
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'attachment/csv;charset=utf-8' }))
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      })
  }

  selecionar(): void {
    this.getData()
  }

  isSuperAdmin(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
  }
}
